import {
  Alert, AlertIcon, Badge, Box, Button, Container, Editable, EditableInput,
  EditablePreview, Flex, FormControl, FormLabel, Heading, HStack, IconButton, Image, Input, Menu, MenuButton,
  MenuDivider, MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Portal, Progress, Skeleton, Spinner, Stack, Text, useDisclosure, useToast,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon, CopyIcon, DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ID3Writer } from 'browser-id3-writer';
import { failArtFor } from '../components/failArt';
import { useAuth } from '../auth/AuthProvider';
import { RootState } from '../redux/store';
import {
  pause as playerPause, play as playerPlay, setQueue, updateTrack, expand, PlayerTrack,
  clearUnavailable,
} from '../redux/features/playerSlice';
import {
  useAddSongToCollectionMutation,
  useCreateCollectionMutation,
  useDeleteCollectionMutation,
  useDeleteJobMutation,
  useRenameCollectionMutation,
  useRenameJobMutation,
  useToggleJobFavouriteMutation,
  useRemoveSongFromCollectionMutation,
} from '../redux/services/mixgenie';
import { useCachedSongs } from '../songs/useCachedSongs';
import { mixgeniePublicServiceURL, mixgenieServiceURL } from '../redux/services/defines';
import { downloadBlobFromUrl } from '../download/downloadBlob';

const ALL = '__all__';

export default function HistoryPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const email = user?.email || '';
  const dispatch = useDispatch();

  // Offline-first: reads localStorage cache synchronously so the page paints
  // real rows immediately, then RTK Query refreshes in the background and
  // mirrors the fresh data back to the cache. Sync also fires on window
  // focus + `online` events. See `songs/songsCache.ts`.
  const {
    songs: jobs,
    collections,
    isFirstLoad,
    isFetchError,
    isSyncing,
    isOffline,
    lastSyncedAt,
  } = useCachedSongs(email);

  const [activeCollectionId, setActiveCollectionId] = useState<string>(ALL);

  const [renameJob] = useRenameJobMutation();
  const [deleteJob] = useDeleteJobMutation();
  const [createCollection] = useCreateCollectionMutation();
  const [renameCollection] = useRenameCollectionMutation();
  const [deleteCollection] = useDeleteCollectionMutation();
  const [addSongToCollection] = useAddSongToCollectionMutation();
  const [removeSongFromCollection] = useRemoveSongFromCollectionMutation();

  const visibleJobs = useMemo(() => {
    if (activeCollectionId === ALL) return jobs;
    const active = collections.find((c: any) => String(c._id) === activeCollectionId);
    if (!active) return [];
    const ids = new Set((active.songIds || []).map((x: any) => String(x)));
    return jobs.filter((j) => ids.has(String(j._id)));
  }, [jobs, collections, activeCollectionId]);

  // Playable tracks for the queue — only completed jobs with an audio URL.
  // The row-level index passed to `playAt` must resolve against THIS array,
  // not against `visibleJobs`, so we build it once here and hand each row
  // its own playable-index (or -1 when it's still generating).
  const playableTracks: PlayerTrack[] = useMemo(() => (
    visibleJobs
      .filter((j) => j.status === 'completed' && (j.outputAudioUrl || j.outputUrl))
      .map((j) => ({
        id: String(j._id),
        title: j.title || t('history.untitled', 'Untitled'),
        artist: 'MixGenie',
        audioUrl: j.outputAudioUrl || j.outputUrl,
        coverUrl: j.coverImageUrl || undefined,
        downloadToken: j.downloadToken || undefined,
        meta: { jobId: String(j._id), lyrics: j.lyrics || undefined, syncedLyrics: j.syncedLyrics || undefined },
      }))
  ), [visibleJobs, t]);

  const playIndexById = useMemo(() => {
    const m = new Map<string, number>();
    playableTracks.forEach((t, i) => m.set(t.id, i));
    return m;
  }, [playableTracks]);

  // When a poll returns fresh data for a track currently in the queue,
  // update it in place so the player picks up new URLs / cover art /
  // renamed titles without interrupting playback. Cover is critical:
  // covers are generated AFTER the song completes, so the queue is
  // usually seeded with `coverUrl: undefined` and only gets the real
  // artwork on a later poll — without this patch, the mini + fullscreen
  // players stay stuck on the fallback gradient forever.
  useEffect(() => {
    for (const t of playableTracks) {
      dispatch(updateTrack({
        id: t.id,
        patch: { audioUrl: t.audioUrl, coverUrl: t.coverUrl, title: t.title, downloadToken: t.downloadToken },
      }));
    }
  }, [playableTracks, dispatch]);

  const playAt = (jobId: string) => {
    const idx = playIndexById.get(jobId);
    if (idx == null || idx < 0) return;
    dispatch(setQueue({ tracks: playableTracks, startIndex: idx }));
  };

  return (
      <Container maxW="720px" py={{ base: 2, md: 4 }} px={0}>
        <Stack spacing={2} mb={5} align="flex-start">
          <HStack spacing={3} align="center">
            <Heading
              as="h1"
              fontSize={{ base: '28px', md: '34px' }}
              fontWeight={800}
              lineHeight="1.1"
              letterSpacing="-0.015em"
              color="whiteAlpha.900"
            >
              {t('history.title', 'My songs')}
            </Heading>
            <SyncPill
              isSyncing={isSyncing}
              isOffline={isOffline}
              isFetchError={isFetchError}
              lastSyncedAt={lastSyncedAt}
              hasCached={jobs.length > 0}
            />
          </HStack>
        </Stack>

        <Flex align="center" justify="flex-end" mb={4}>
          <NewCollectionButton onCreate={(name) => createCollection({ name }).unwrap()} />
        </Flex>

        <PlaylistTabs
          collections={collections}
          activeId={activeCollectionId}
          onSelect={setActiveCollectionId}
          onRename={(id, name) => renameCollection({ id, name }).unwrap()}
          onDelete={async (id) => {
            await deleteCollection({ id }).unwrap();
            if (activeCollectionId === id) setActiveCollectionId(ALL);
          }}
        />

        {isFirstLoad && (
          <Stack spacing={2}>
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                height="82px"
                borderRadius="14px"
                startColor="rgba(20, 14, 36, 0.8)"
                endColor="rgba(46, 18, 64, 0.8)"
              />
            ))}
          </Stack>
        )}
        {!isFirstLoad && isFetchError && jobs.length === 0 && (
          <Alert
            status="error"
            borderRadius="14px"
            bg="rgba(60, 20, 30, 0.6)"
            backdropFilter="blur(10px)"
            color="whiteAlpha.900"
            border="1px solid"
            borderColor="red.500"
          >
            <AlertIcon color="red.300" />
            {t('history.loadError', 'Failed to load your songs.')}
          </Alert>
        )}
        {!isFirstLoad && !isFetchError && visibleJobs.length === 0 && (
          <Box
            bg="rgba(20, 14, 36, 0.55)"
            backdropFilter="blur(14px)"
            border="1px solid"
            borderColor="whiteAlpha.150"
            borderRadius="18px"
            p={{ base: 6, md: 8 }}
            textAlign="center"
          >
            <Text color="whiteAlpha.700" fontSize={{ base: 'sm', md: 'md' }} mb={4}>
              {activeCollectionId === ALL
                ? t('history.empty', "You haven't created any songs yet.")
                : t('history.emptyPlaylist', 'This playlist is empty. Add songs from All songs.')}
            </Text>
            {activeCollectionId === ALL && (
              <Button
                as={RouterLink}
                to="/generator"
                size="md"
                borderRadius="12px"
                bgGradient="linear(90deg, #E94A8F 0%, #7354FF 100%)"
                color="white"
                fontWeight={700}
                _hover={{
                  bgGradient: 'linear(90deg, #F35DA0 0%, #8567FF 100%)',
                  transform: 'translateY(-1px)',
                }}
                boxShadow="0 10px 24px rgba(233,74,143,0.28)"
                data-cta="songs:empty-create"
              >
                {t('history.emptyCta', 'Create your first song')}
              </Button>
            )}
          </Box>
        )}

        <Stack spacing={2}>
          {visibleJobs.map((job) => (
            <JobRow
              key={job._id || job.id}
              job={job}
              collections={collections}
              activeCollectionId={activeCollectionId}
              onRename={(title) => renameJob({ jobId: String(job._id), title }).unwrap()}
              onAddToCollection={(collectionId) => addSongToCollection({ collectionId, jobId: String(job._id) }).unwrap()}
              onCreateAndAddCollection={async (name) => {
                // Create the playlist, then add this song to it in one shot.
                // The `unwrap()` on create surfaces the new id; addSong is
                // fire-and-forget after that — RTK invalidation refreshes
                // both lists on completion.
                const created: any = await createCollection({ name }).unwrap();
                const newId = created?._id || created?.id || created?.data?._id || created?.data?.id;
                if (newId) {
                  await addSongToCollection({ collectionId: String(newId), jobId: String(job._id) }).unwrap();
                }
              }}
              onRemoveFromCollection={(collectionId) => removeSongFromCollection({ collectionId, jobId: String(job._id) }).unwrap()}
              onDelete={() => deleteJob({ jobId: String(job._id) }).unwrap()}
              onPlay={() => playAt(String(job._id))}
              t={t as any}
            />
          ))}
        </Stack>
      </Container>
  );
}

// ---------------------------------------------------------------------------
// Sync / offline indicator
// ---------------------------------------------------------------------------
function SyncPill({
  isSyncing,
  isOffline,
  isFetchError,
  lastSyncedAt,
  hasCached,
}: {
  isSyncing: boolean;
  isOffline: boolean;
  isFetchError: boolean;
  lastSyncedAt: number;
  hasCached: boolean;
}) {
  // Priority: offline > sync-error > syncing > synced
  let label = '';
  let color = 'whiteAlpha.700';
  let dotColor = 'whiteAlpha.500';
  if (isOffline) {
    label = 'Offline';
    color = 'orange.200';
    dotColor = 'orange.300';
  } else if (isFetchError && hasCached) {
    label = 'Sync error';
    color = 'orange.200';
    dotColor = 'orange.300';
  } else if (isSyncing && hasCached) {
    label = 'Syncing';
    color = 'whiteAlpha.700';
    dotColor = 'pink.300';
  } else if (lastSyncedAt) {
    const diff = Date.now() - lastSyncedAt;
    if (diff < 60_000) label = 'Synced';
    else if (diff < 3600_000) label = `Synced ${Math.floor(diff / 60_000)}m ago`;
    else label = `Synced ${Math.floor(diff / 3600_000)}h ago`;
    color = 'whiteAlpha.600';
    dotColor = 'green.300';
  } else {
    return null;
  }
  return (
    <HStack
      spacing={2}
      px={2.5}
      py={1}
      borderRadius="full"
      bg="rgba(20, 14, 36, 0.6)"
      border="1px solid"
      borderColor="whiteAlpha.150"
      fontFamily="ui-monospace, SF Mono, Menlo, monospace"
      fontSize="10px"
      letterSpacing="0.06em"
      textTransform="uppercase"
      color={color}
      aria-live="polite"
    >
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        bg={dotColor}
        boxShadow={isSyncing ? `0 0 8px currentColor` : undefined}
      />
      <Text as="span">{label}</Text>
    </HStack>
  );
}

// ---------------------------------------------------------------------------
// Playlist tabs
// ---------------------------------------------------------------------------
function PlaylistTabs({
  collections,
  activeId,
  onSelect,
  onRename,
  onDelete,
}: {
  collections: any[];
  activeId: string;
  onSelect: (id: string) => void;
  onRename: (id: string, name: string) => Promise<any>;
  onDelete: (id: string) => Promise<any>;
}) {
  const toast = useToast();
  const managing = useDisclosure();
  return (
    <Box mb={6}>
      <HStack spacing={2} overflowX="auto" pb={2}>
        <TabPill label="All songs" active={activeId === ALL} onClick={() => onSelect(ALL)} />
        {collections.map((c: any) => (
          <TabPill
            key={c._id}
            label={c.name}
            count={c.songIds?.length || 0}
            active={activeId === c._id}
            onClick={() => onSelect(c._id)}
          />
        ))}
        {collections.length > 0 && (
          <IconButton
            aria-label="Manage playlists"
            icon={<EditIcon />}
            size="sm"
            variant="ghost"
            color="whiteAlpha.700"
            onClick={managing.onOpen}
          />
        )}
      </HStack>
      <Modal isOpen={managing.isOpen} onClose={managing.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>Manage playlists</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={3}>
              {collections.map((c: any) => (
                <HStack key={c._id} spacing={2}>
                  <Editable
                    defaultValue={c.name}
                    onSubmit={async (v) => {
                      const next = (v || '').trim();
                      if (next && next !== c.name) {
                        try { await onRename(c._id, next); } catch (e: any) {
                          toast({ title: e?.data?.message || 'Rename failed', status: 'error' });
                        }
                      }
                    }}
                    submitOnBlur
                    flex="1"
                  >
                    <EditablePreview px={3} py={2} borderRadius="8px" bg="whiteAlpha.100" w="full" />
                    <EditableInput px={3} py={2} borderRadius="8px" bg="whiteAlpha.100" />
                  </Editable>
                  <IconButton
                    aria-label="Delete playlist"
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={async () => {
                      if (!confirm(`Delete playlist "${c.name}"? Songs stay in All songs.`)) return;
                      try { await onDelete(c._id); } catch (e: any) {
                        toast({ title: e?.data?.message || 'Delete failed', status: 'error' });
                      }
                    }}
                  />
                </HStack>
              ))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={managing.onClose} variant="ghost" colorScheme="whiteAlpha">Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function TabPill({ label, count, active, onClick }: { label: string; count?: number; active: boolean; onClick: () => void }) {
  return (
    <Button
      size="sm"
      onClick={onClick}
      borderRadius="full"
      flexShrink={0}
      fontWeight={active ? 700 : 500}
      px={4}
      bgGradient={active ? 'linear(90deg, #E94A8F 0%, #7354FF 100%)' : undefined}
      bg={active ? undefined : 'rgba(20, 14, 36, 0.55)'}
      color={active ? 'white' : 'whiteAlpha.800'}
      border="1px solid"
      borderColor={active ? 'transparent' : 'whiteAlpha.150'}
      boxShadow={active ? '0 6px 18px rgba(233,74,143,0.28)' : 'none'}
      _hover={{
        bgGradient: active ? 'linear(90deg, #F35DA0 0%, #8567FF 100%)' : undefined,
        bg: active ? undefined : 'rgba(30, 20, 48, 0.7)',
        borderColor: active ? 'transparent' : 'whiteAlpha.300',
      }}
    >
      {label}
      {count != null && (
        <Text as="span" ml={2} fontSize="xs" opacity={0.8}>
          {count}
        </Text>
      )}
    </Button>
  );
}

function NewCollectionButton({ onCreate }: { onCreate: (name: string) => Promise<any> }) {
  const toast = useToast();
  const modal = useDisclosure();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <>
      <Button size="sm" leftIcon={<AddIcon />} colorScheme="pink" variant="outline" onClick={modal.onOpen}>
        New playlist
      </Button>
      <Modal isOpen={modal.isOpen} onClose={modal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>New playlist</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Input
              placeholder="e.g. Chill vibes"
              value={name}
              onChange={(e) => setName(e.target.value)}
              bg="whiteAlpha.100"
              borderColor="whiteAlpha.300"
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="pink"
              isLoading={busy}
              isDisabled={!name.trim()}
              onClick={async () => {
                setBusy(true);
                try {
                  await onCreate(name.trim());
                  setName('');
                  modal.onClose();
                } catch (e: any) {
                  toast({ title: e?.data?.message || 'Create failed', status: 'error' });
                } finally {
                  setBusy(false);
                }
              }}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// ---------------------------------------------------------------------------
// Single song row — SoundCloud-style
// ---------------------------------------------------------------------------
function JobRow({
  job,
  collections,
  activeCollectionId,
  onRename,
  onAddToCollection,
  onCreateAndAddCollection,
  onRemoveFromCollection,
  onDelete,
  onPlay,
  t,
}: {
  job: any;
  collections: any[];
  activeCollectionId: string;
  onRename: (title: string) => Promise<any>;
  onAddToCollection: (collectionId: string) => Promise<any>;
  onCreateAndAddCollection: (name: string) => Promise<any>;
  onRemoveFromCollection: (collectionId: string) => Promise<any>;
  onDelete: () => Promise<any>;
  onPlay: () => void;
  t: (k: string, d?: string) => string;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const jobId = String(job._id);
  const audioUrl: string | undefined = job.outputAudioUrl || job.outputUrl;
  const createdAt = job.createdAt ? new Date(job.createdAt) : null;
  const displayTitle = job.title || t('history.untitled', 'Untitled');

  const player = useSelector((s: RootState) => s.player);
  const currentTrackId = player.index >= 0 ? player.queue[player.index]?.id : null;
  const isCurrent = currentTrackId === jobId;
  const isPlaying = isCurrent && player.isPlaying;
  const progressPct = isCurrent && player.duration > 0
    ? (player.progress / player.duration) * 100
    : 0;
  // Session-only flag — set by GlobalAudioPlayer when this track failed
  // to load repeatedly. Auto-expires after UNAVAILABLE_TTL so a transient
  // network blip (mobile tower handoff, brief CDN hiccup) doesn't stick
  // "UNAVAILABLE" on a working row until the user reloads the app.
  const UNAVAILABLE_TTL_MS = 5 * 60 * 1000;
  const markedAt = player.unavailableAt?.[jobId] || 0;
  const isUnavailable = player.unavailableIds.includes(jobId)
    && markedAt > 0
    && (Date.now() - markedAt) < UNAVAILABLE_TTL_MS;

  const hasAudio = job.status === 'completed' && !!audioUrl;
  const canPlay = hasAudio && !isUnavailable;

  const handleTogglePlay = () => {
    // If the row is showing UNAVAILABLE, treat the tap as an explicit
    // "try again" — clear the mark and attempt play. Costs nothing if
    // it fails (GlobalAudioPlayer will re-mark it after MAX_ATTEMPTS+1
    // failures) and rescues the common case where the URL was fine and
    // the previous failure was network-transient.
    if (isUnavailable) {
      dispatch(clearUnavailable(jobId));
      if (hasAudio) onPlay();
      return;
    }
    if (!canPlay) return;
    if (isCurrent) {
      dispatch(isPlaying ? playerPause() : playerPlay());
    } else {
      onPlay();
    }
  };

  // Double-click / double-tap: play + open the full-screen player. This
  // is the standard media-app pattern (Spotify, Apple Music) — single
  // tap toggles playback in the row, double tap opens the immersive view.
  const handleOpenFull = () => {
    if (!canPlay) return;
    if (!isCurrent) onPlay();
    dispatch(expand());
  };

  const goGenerateSimilar = () => {
    const params = new URLSearchParams();
    if (job.referenceTitle) params.set('referenceMode', 'title-artist');
    if (job.referenceTitle) params.set('referenceTitle', job.referenceTitle);
    if (job.referenceArtist) params.set('referenceArtist', job.referenceArtist);
    if (job.referenceSlug) params.set('referenceSlug', job.referenceSlug);
    if (job.referenceArtistSlug) params.set('referenceArtistSlug', job.referenceArtistSlug);
    if (job.prompt) params.set('vibe', job.prompt);
    else if (job.vibe) params.set('vibe', job.vibe);
    navigate(`/generator?${params.toString()}`);
  };

  const inCollectionsForSong = collections.filter((c: any) =>
    (c.songIds || []).some((sid: any) => String(sid) === jobId),
  );

  // "+ Create new playlist" from inside the row's ⋮ menu — creates the
  // playlist AND adds this song to it in one step, so the user doesn't
  // have to close the menu, tap the top-of-page button, name it, then
  // come back and add.
  const newPlaylistModal = useDisclosure();
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistBusy, setNewPlaylistBusy] = useState(false);
  const submitNewPlaylist = async () => {
    const name = newPlaylistName.trim();
    if (!name) return;
    setNewPlaylistBusy(true);
    try {
      await onCreateAndAddCollection(name);
      setNewPlaylistName('');
      newPlaylistModal.onClose();
      toast({ title: t('history.playlistCreated', 'Playlist created — song added'), status: 'success', duration: 1800 });
    } catch (e: any) {
      toast({ title: e?.data?.message || t('history.playlistCreateFail', 'Could not create playlist'), status: 'error' });
    } finally {
      setNewPlaylistBusy(false);
    }
  };

  // Heart toggle — persists to the job doc via PATCH /:jobId. Optimistic
  // UX would be nice but the mutation is fast enough (~200ms) that a
  // brief spinner via `favBusy` reads fine. The Faves tab surface still
  // shows only catalog favourites for now — extending it to include
  // generated-song favourites is a follow-up.
  const isFavourited = !!job.favourited;
  const [toggleFavMut, { isLoading: favBusy }] = useToggleJobFavouriteMutation();
  const toggleFav = async () => {
    try {
      await toggleFavMut({ jobId, favourited: !isFavourited }).unwrap();
    } catch (e: any) {
      toast({ title: e?.data?.message || t('history.favFail', 'Could not update — try again'), status: 'error' });
    }
  };

  // Share via the public /s/:jobId page — recipients can play without an
  // account. Uses navigator.share on mobile (native sheet, biggest
  // conversion surface), falls back to clipboard-copy on desktop.
  const shareSong = async () => {
    const url = `${window.location.origin}/s/${jobId}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: displayTitle,
          text: `Listen to "${displayTitle}" — made with MixGenie`,
          url,
        });
      } catch { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast({ title: t('history.linkCopied', 'Share link copied'), status: 'success', duration: 1800 });
      } catch {
        toast({ title: url, status: 'info', duration: 4000 });
      }
    }
  };

  const downloadWithTags = async () => {
    if (!audioUrl) return;
    // Fetch-as-blob then click a same-origin `blob:` URL. See
    // src/download/downloadBlob.ts for full rationale — short version: mobile
    // Chrome ignores `<a download>` on cross-origin URLs and opens the audio
    // in an in-app Custom Tab preview instead of saving.
    const token = (job as any).downloadToken;
    if (!token) {
      toast({ title: t('history.downloadTokenMissing', 'Refresh the page and try again'), status: 'warning', duration: 2500 });
      return;
    }
    const proxyUrl = `${mixgeniePublicServiceURL}/songs/${jobId}/audio.mp3?tok=${encodeURIComponent(token)}`;
    try {
      await downloadBlobFromUrl(proxyUrl, `${sanitizeFilename(displayTitle)}.mp3`);
    } catch (e: any) {
      toast({ title: t('history.downloadFailed', 'Download failed'), description: String(e?.message || e), status: 'error', duration: 3000 });
    }
  };

  const renameModal = useDisclosure();
  const lyricsModal = useDisclosure();
  const hasLyrics = !!(job.lyrics && String(job.lyrics).trim());
  const copyLyrics = async () => {
    if (!hasLyrics) return;
    try {
      await navigator.clipboard.writeText(String(job.lyrics));
      toast({ title: t('history.lyricsCopied', 'Lyrics copied'), status: 'success', duration: 1500 });
    } catch {
      toast({ title: t('history.lyricsCopyFail', 'Copy failed — select the text manually'), status: 'warning', duration: 2500 });
    }
  };
  const [renameDraft, setRenameDraft] = useState(displayTitle);
  const [renameBusy, setRenameBusy] = useState(false);
  const openRename = () => { setRenameDraft(displayTitle); renameModal.onOpen(); };
  const submitRename = async () => {
    const next = renameDraft.trim();
    if (!next || next === displayTitle) { renameModal.onClose(); return; }
    setRenameBusy(true);
    try {
      await onRename(next);
      renameModal.onClose();
    } catch (e: any) {
      toast({ title: e?.data?.message || 'Rename failed', status: 'error' });
    } finally {
      setRenameBusy(false);
    }
  };

  return (
    <Box
      as={canPlay ? 'button' : 'div'}
      w="full"
      textAlign="left"
      onClick={canPlay ? handleTogglePlay : undefined}
      onDoubleClick={canPlay ? handleOpenFull : undefined}
      bg={isUnavailable
        ? 'rgba(20, 14, 36, 0.35)'
        : isCurrent ? 'rgba(60, 30, 80, 0.75)' : 'rgba(20, 14, 36, 0.55)'}
      backdropFilter="blur(14px)"
      border="1px solid"
      borderColor={isUnavailable
        ? 'whiteAlpha.100'
        : isCurrent ? 'pink.400' : 'whiteAlpha.150'}
      borderRadius="16px"
      px={{ base: 3, md: 4 }}
      py={3}
      opacity={isUnavailable ? 0.55 : 1}
      transition="background 0.15s, border-color 0.15s, opacity 0.15s"
      cursor={canPlay ? 'pointer' : 'default'}
      boxShadow={isCurrent
        ? '0 12px 32px rgba(233,74,143,0.25)'
        : '0 4px 14px rgba(0,0,0,0.15)'}
      _hover={{
        bg: isUnavailable
          ? 'rgba(20, 14, 36, 0.45)'
          : isCurrent ? 'rgba(60, 30, 80, 0.85)' : 'rgba(30, 20, 48, 0.7)',
        borderColor: isUnavailable
          ? 'whiteAlpha.150'
          : isCurrent ? 'pink.300' : 'whiteAlpha.300',
      }}
      _focus={{ outline: '2px solid', outlineColor: 'pink.400', outlineOffset: '2px' }}
    >
      <Flex align="center" gap={3}>
        {/* Cover art with always-visible play/pause icon overlay */}
        <Box position="relative" flexShrink={0}>
          <Box
            boxSize={{ base: '52px', md: '60px' }}
            borderRadius="8px"
            bgGradient={isCurrent
              ? 'linear(135deg, pink.400, purple.500)'
              : 'linear(135deg, pink.500, purple.600)'}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            fontSize="2xl"
            opacity={canPlay ? 1 : 0.55}
            overflow="hidden"
          >
            {job.status === 'failed'
              ? <Image src={failArtFor(jobId)} boxSize="full" objectFit="cover" alt={t('history.failedArt', 'This job has failed')} draggable={false} style={{ opacity: 1 }} />
              : (job.coverThumbnailUrl || job.coverImageUrl)
              ? <Image src={job.coverThumbnailUrl || job.coverImageUrl} boxSize="full" objectFit="cover" alt="" draggable={false} />
              : (job.instrumental ? '🎹' : '🎵')}
          </Box>
          {canPlay && (
            <Flex
              position="absolute"
              inset={0}
              align="center"
              justify="center"
              bg="blackAlpha.500"
              borderRadius="8px"
              color="white"
              opacity={isCurrent ? 1 : 0.85}
              pointerEvents="none"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                <path d={isPlaying ? 'M6 5h4v14H6zM14 5h4v14h-4z' : 'M8 5v14l11-7z'} />
              </svg>
            </Flex>
          )}
        </Box>

        {/* Title + meta */}
        <Box flex="1" minW={0}>
          <Text
            fontSize="md"
            fontWeight={700}
            color="whiteAlpha.900"
            noOfLines={1}
          >
            {displayTitle}
          </Text>
          <HStack spacing={2} color="whiteAlpha.600" fontSize="xs" mt={0.5} wrap="wrap">
            <Text noOfLines={1}>MixGenie</Text>
            {createdAt && <Text>· {createdAt.toLocaleDateString()}</Text>}
            {isUnavailable && (
              <Badge colorScheme="orange" fontSize="9px" textTransform="uppercase" letterSpacing="0.06em">
                {t('history.unavailable', 'Unavailable')}
              </Badge>
            )}
            {job.instrumental && <Badge colorScheme="purple" fontSize="9px">instrumental</Badge>}
            {(job.referenceTitle || job.referenceArtist) && (
              <Text as="span" noOfLines={1} color="whiteAlpha.500">
                · from {job.referenceTitle}{job.referenceArtist ? ` — ${job.referenceArtist}` : ''}
              </Text>
            )}
            {job.status === 'processing' && (
              <HStack spacing={1} color="yellow.300">
                <Spinner size="xs" />
                <Text>{t('history.processing', 'Generating…')}</Text>
              </HStack>
            )}
            {job.status === 'failed' && (
              <Badge colorScheme="red" fontSize="9px">failed</Badge>
            )}
          </HStack>
          {/* Live progress bar — only for the currently playing track */}
          {isCurrent && (
            <Progress
              value={progressPct}
              size="xs"
              colorScheme="pink"
              bg="whiteAlpha.200"
              borderRadius="full"
              mt={2}
            />
          )}
        </Box>

        {/* Actions — stop click propagation so pressing the menu / download
            button doesn't also trigger the row's tap-to-play. */}
        <HStack spacing={1} flexShrink={0} onClick={(e) => e.stopPropagation()}>
          {canPlay && (
            <IconButton
              aria-label={isFavourited ? 'Remove from favourites' : 'Add to favourites'}
              onClick={(e) => { e.stopPropagation(); if (!favBusy) toggleFav(); }}
              isDisabled={favBusy}
              variant="ghost"
              color={isFavourited ? 'pink.300' : 'whiteAlpha.700'}
              _hover={{ color: 'pink.300', bg: 'whiteAlpha.100' }}
              size="sm"
              icon={
                isFavourited ? (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M12 21s-7.5-4.5-9.5-9C1 8.5 3.5 5 7 5c2 0 3.5 1 5 3 1.5-2 3-3 5-3 3.5 0 6 3.5 4.5 7-2 4.5-9.5 9-9.5 9z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                    <path d="M12 21s-7.5-4.5-9.5-9C1 8.5 3.5 5 7 5c2 0 3.5 1 5 3 1.5-2 3-3 5-3 3.5 0 6 3.5 4.5 7-2 4.5-9.5 9-9.5 9z" />
                  </svg>
                )
              }
            />
          )}
          {canPlay && (
            <IconButton
              aria-label="Share"
              onClick={(e) => { e.stopPropagation(); shareSong(); }}
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'pink.300', bg: 'whiteAlpha.100' }}
              size="sm"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                </svg>
              }
            />
          )}
          <Menu placement="bottom-end">
            <MenuButton
              as={IconButton}
              aria-label="More"
              variant="ghost"
              color="whiteAlpha.700"
              _hover={{ color: 'pink.300', bg: 'whiteAlpha.100' }}
              size="sm"
              icon={
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                </svg>
              }
            />
            <Portal>
            <MenuList bg="night.800" borderColor="whiteAlpha.300" minW="240px" zIndex={2000}>
              {canPlay && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  onClick={shareSong}
                  icon={<Text as="span">↗</Text>}
                >
                  {t('history.share', 'Share')}
                </MenuItem>
              )}
              <MenuItem
                bg="night.800"
                color="whiteAlpha.900"
                _hover={{ bg: 'whiteAlpha.200' }}
                _focus={{ bg: 'whiteAlpha.200' }}
                onClick={openRename}
                icon={<EditIcon />}
              >
                {t('history.rename', 'Rename')}
              </MenuItem>
              {hasLyrics && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  onClick={lyricsModal.onOpen}
                  icon={<Text as="span">📝</Text>}
                >
                  {t('history.viewLyrics', 'View lyrics')}
                </MenuItem>
              )}
              {canPlay && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  onClick={goGenerateSimilar}
                  icon={<Text as="span">✨</Text>}
                >
                  {t('history.generateSimilar', 'Generate similar')}
                </MenuItem>
              )}
              {canPlay && (
                <MenuItem
                  bg="night.800"
                  color="whiteAlpha.900"
                  _hover={{ bg: 'whiteAlpha.200' }}
                  _focus={{ bg: 'whiteAlpha.200' }}
                  onClick={downloadWithTags}
                  icon={<Text as="span">⬇</Text>}
                >
                  {t('history.download', 'Download')}
                </MenuItem>
              )}
              <MenuDivider borderColor="whiteAlpha.200" />
              <Box px={3} py={1}>
                <Text fontSize="xs" color="whiteAlpha.500" textTransform="uppercase">
                  {t('history.playlists', 'Playlists')}
                </Text>
              </Box>
              <MenuItem
                bg="night.800"
                color="accent.400"
                _hover={{ bg: 'whiteAlpha.200' }}
                _focus={{ bg: 'whiteAlpha.200' }}
                onClick={() => { setNewPlaylistName(''); newPlaylistModal.onOpen(); }}
                icon={<AddIcon />}
              >
                {t('history.newPlaylistFromSong', 'New playlist with this song')}
              </MenuItem>
              {collections.length === 0 && (
                <Box px={3} py={2}>
                  <Text fontSize="xs" color="whiteAlpha.600">{t('history.noPlaylists', 'No other playlists yet.')}</Text>
                </Box>
              )}
              {collections.map((c: any) => {
                const inThis = inCollectionsForSong.some((x: any) => String(x._id) === String(c._id));
                return (
                  <MenuItem
                    key={c._id}
                    bg="night.800"
                    color="whiteAlpha.900"
                    _hover={{ bg: 'whiteAlpha.200' }}
                    _focus={{ bg: 'whiteAlpha.200' }}
                    onClick={async () => {
                      try {
                        if (inThis) await onRemoveFromCollection(c._id);
                        else await onAddToCollection(c._id);
                      } catch (e: any) {
                        toast({ title: e?.data?.message || 'Update failed', status: 'error' });
                      }
                    }}
                  >
                    {inThis ? '✓ ' : ''}{c.name}
                  </MenuItem>
                );
              })}
              {activeCollectionId !== ALL && inCollectionsForSong.some((x: any) => String(x._id) === activeCollectionId) && (
                <>
                  <MenuDivider borderColor="whiteAlpha.200" />
                  <MenuItem
                    bg="night.800"
                    color="red.300"
                    _hover={{ bg: 'red.900' }}
                    _focus={{ bg: 'red.900' }}
                    onClick={async () => {
                      try { await onRemoveFromCollection(activeCollectionId); } catch (e: any) {
                        toast({ title: e?.data?.message || 'Remove failed', status: 'error' });
                      }
                    }}
                  >
                    Remove from this playlist
                  </MenuItem>
                </>
              )}
              <MenuDivider borderColor="whiteAlpha.200" />
              <MenuItem
                icon={<DeleteIcon />}
                bg="night.800"
                color="red.300"
                _hover={{ bg: 'red.900' }}
                _focus={{ bg: 'red.900' }}
                onClick={async () => {
                  const ok = window.confirm(
                    t('history.confirmDelete', `Delete "${displayTitle}"? This removes the song and its audio permanently.`),
                  );
                  if (!ok) return;
                  try {
                    await onDelete();
                    toast({ title: t('history.deleted', 'Song deleted'), status: 'success', duration: 2000 });
                  } catch (e: any) {
                    toast({ title: e?.data?.message || 'Delete failed', status: 'error' });
                  }
                }}
              >
                {t('history.delete', 'Delete song')}
              </MenuItem>
            </MenuList>
            </Portal>
          </Menu>
        </HStack>
      </Flex>

      {job.status === 'failed' && job.errorMessage && (
        <Alert status="error" borderRadius="8px" mt={3} bg="red.900" color="whiteAlpha.900" borderWidth="1px" borderColor="red.500" fontSize="sm" onClick={(e) => e.stopPropagation()}>
          <AlertIcon color="red.300" />
          {job.errorMessage}
        </Alert>
      )}

      <Modal isOpen={renameModal.isOpen} onClose={renameModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900" onClick={(e) => e.stopPropagation()}>
          <ModalHeader>{t('history.renameTitle', 'Rename song')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm" color="whiteAlpha.700">
                {t('history.renameLabel', 'Song title')}
              </FormLabel>
              <Input
                value={renameDraft}
                onChange={(e) => setRenameDraft(e.target.value)}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme="whiteAlpha" onClick={renameModal.onClose} mr={2}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              colorScheme="pink"
              onClick={submitRename}
              isLoading={renameBusy}
              isDisabled={!renameDraft.trim() || renameDraft.trim() === displayTitle}
            >
              {t('save', 'Save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={lyricsModal.isOpen} onClose={lyricsModal.onClose} isCentered size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900" onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <Stack spacing={0}>
              <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="0.08em">
                {t('history.lyricsFor', 'Lyrics')}
              </Text>
              <Text fontSize="md" color="whiteAlpha.900" noOfLines={2}>{displayTitle}</Text>
            </Stack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box
              as="pre"
              whiteSpace="pre-wrap"
              wordBreak="break-word"
              fontFamily="body"
              fontSize={{ base: 'sm', md: 'md' }}
              lineHeight="1.7"
              color="whiteAlpha.900"
              bg="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.150"
              borderRadius="10px"
              p={4}
              m={0}
            >
              {job.lyrics}
            </Box>
          </ModalBody>
          <ModalFooter gap={2} flexWrap="wrap">
            <Button
              leftIcon={<CopyIcon />}
              variant="ghost"
              colorScheme="whiteAlpha"
              onClick={copyLyrics}
            >
              {t('history.copyLyrics', 'Copy')}
            </Button>
            <Button
              variant="outline"
              colorScheme="pink"
              onClick={() => {
                lyricsModal.onClose();
                // Route via state (not URL) — lyrics can be a few thousand
                // chars, which would blow past reasonable URL length limits
                // and break some proxies. Style/reference/presets are
                // deliberately NOT carried over: the whole point is a new
                // musical treatment of the same words.
                navigate('/generator', { state: { lyrics: String(job.lyrics || '') } });
              }}
            >
              {t('history.newSongWithLyrics', 'New song with these lyrics')}
            </Button>
            <Button colorScheme="pink" onClick={lyricsModal.onClose}>
              {t('close', 'Close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create-new-playlist-with-this-song modal — spawned from the ⋮
          menu's playlists section. Same shape as the top-of-page
          NewCollectionButton, but the callback creates + adds in one
          shot instead of just creating. */}
      <Modal isOpen={newPlaylistModal.isOpen} onClose={newPlaylistModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="night.800" color="whiteAlpha.900">
          <ModalHeader>{t('history.newPlaylistFromSong', 'New playlist with this song')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel fontSize="sm" color="whiteAlpha.700">
                {t('history.playlistNameLabel', 'Playlist name')}
              </FormLabel>
              <Input
                value={newPlaylistName}
                onChange={(e) => setNewPlaylistName(e.target.value)}
                placeholder={t('history.playlistNamePlaceholder', 'e.g. Chill vibes') as string}
                bg="whiteAlpha.100"
                borderColor="whiteAlpha.300"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') submitNewPlaylist(); }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" colorScheme="whiteAlpha" onClick={newPlaylistModal.onClose} mr={2}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              colorScheme="pink"
              onClick={submitNewPlaylist}
              isLoading={newPlaylistBusy}
              isDisabled={!newPlaylistName.trim()}
            >
              {t('history.playlistCreateAndAdd', 'Create & add')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

function sanitizeFilename(name: string): string {
  return (name || 'song').replace(/[^\w\-. ()]+/g, '_').slice(0, 80).trim() || 'song';
}
